import React, { useEffect } from 'react';
import { MapPinIcon, NavigationIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
interface AddressMismatchModalProps {
  isOpen: boolean;
  selectedAddress: string;
  detectedLocation: string;
  onClose: () => void;
  onConfirm: () => void;
  onChangeAddress: () => void;
}
export function AddressMismatchModal({
  isOpen,
  selectedAddress,
  detectedLocation,
  onClose,
  onConfirm,
  onChangeAddress
}: AddressMismatchModalProps) {
  const shouldReduceMotion = useReducedMotion();
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-2998" data-mp="38x9mje1t76ht">
      {isOpen && <div className="fixed inset-0 z-[210] flex items-end md:items-center md:justify-center md:p-6" role="dialog" aria-modal="true" aria-labelledby="address-mismatch-title" data-id="element-2999" data-mp="33xewtdtrsmyc">
          <motion.button type="button" aria-label="Close address confirmation" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: shouldReduceMotion ? 0 : 0.18,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute inset-0 h-full w-full cursor-default bg-slate-950/45" data-id="element-3000" data-mp="2h38l5t46vnd1" />
          <motion.section initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 28
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 18
      }} transition={{
        duration: shouldReduceMotion ? 0 : 0.24,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative w-full rounded-t-2xl bg-white px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4 shadow-2xl md:max-w-md md:rounded-2xl md:p-7" data-id="element-3001" data-mp="2zsoo0n3cjrwm">
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-gray-200 md:hidden" data-id="element-3002" data-mp="17ei1yj3ysvd2" />
            <div className="flex items-start justify-between gap-4" data-id="element-3003" data-mp="12f5jsbo1gcgt">
              <div className="flex items-start gap-3" data-id="element-3004" data-mp="1hbmsev4ufczo">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#fdf2f2] text-[#bd3434]" data-id="element-3005" data-mp="1hd7legbienvf">
                  <NavigationIcon className="h-5 w-5" data-id="element-3006" data-mp="1cewby6p4mfdq" />
                </span>
                <div data-id="element-3007" data-mp="1qai0kcrg6x5m">
                  <h2 id="address-mismatch-title" className="text-xl font-bold tracking-tight text-gray-900" data-id="element-3008" data-mp="1ld80822it130">Is this your delivery address?</h2>
                  <p className="mt-1 text-sm leading-5 text-gray-600" data-id="element-3009" data-mp="2012uvo039fiq">Your selected address looks different from your current location.</p>
                </div>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900" data-id="element-3010" data-mp="292ass4yp0eca">
                <XIcon className="h-5 w-5" data-id="element-3011" data-mp="2nq11s6ubb5ya" />
              </button>
            </div>

            <div className="mt-5 space-y-3 rounded-xl bg-[#f8f9fc] p-4" data-id="element-3012" data-mp="2snj9jtwlff4p">
              <div className="flex gap-3" data-id="element-3013" data-mp="2epclt6lcyvir">
                <MapPinIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#1E2B55]" data-id="element-3014" data-mp="2eobdh9vq8n4q" />
                <div data-id="element-3015" data-mp="2jml55ai8t5ll">
                  <p className="text-xs font-semibold text-gray-500" data-id="element-3016" data-mp="2jm34m1ojs57v">SELECTED DELIVERY ADDRESS</p>
                  <p className="mt-1 text-sm font-bold text-gray-900" data-id="element-3017" data-mp="24xoxvfo0fwm1">{selectedAddress}</p>
                </div>
              </div>
              <div className="ml-2 h-4 border-l border-dashed border-gray-300" aria-hidden="true" data-id="element-3018" data-mp="1vylh4icbftap" />
              <div className="flex gap-3" data-id="element-3019" data-mp="1zznj36xmajgd">
                <NavigationIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#1E2B55]" data-id="element-3020" data-mp="200q97cn48hv8" />
                <div data-id="element-3021" data-mp="1m171um24pajz">
                  <p className="text-xs font-semibold text-gray-500" data-id="element-3022" data-mp="1m0xl7p2z4fqy">CURRENT LOCATION</p>
                  <p className="mt-1 text-sm font-medium text-gray-800" data-id="element-3023" data-mp="1qypbyfrvtvtk">{detectedLocation}</p>
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2" data-id="element-3024" data-mp="2pbbgid2p63bb">
              <button type="button" onClick={onConfirm} className="order-2 h-12 rounded-lg border border-gray-300 px-4 text-sm font-bold text-gray-800 transition-colors duration-150 hover:bg-gray-50 sm:order-1" data-id="element-3025" data-mp="2pd5ypjml3hbp">
                Confirm address
              </button>
              <button type="button" onClick={onChangeAddress} className="order-1 h-12 rounded-lg bg-[#1E2B55] px-4 text-sm font-bold text-white transition-colors duration-150 hover:bg-[#162041] sm:order-2" data-id="element-3026" data-mp="2amyfv2snzwah">
                Change address
              </button>
            </div>
          </motion.section>
        </div>}
    </AnimatePresence>;
}